import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from 'recharts';
import {
  Calculator, TrendingUp, Megaphone, Building2, ArrowUpRight, ArrowDownRight,
  AlertTriangle, ShieldAlert, Clock, CreditCard, Ticket, Users, DollarSign,
  Download, RefreshCw, ChevronDown
} from 'lucide-react';
import api from '../../api/client';
import { toast } from 'sonner';
import { useCurrency } from '../../context/CurrencyContext';
import { formatAmount } from '../../lib/currencyUtils';

const COLORS = ['#561D1A', '#D4AF37', '#2563EB', '#059669', '#DC2626', '#7C3AED', '#F59E0B', '#6366F1'];
const PERIODS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: '365d', label: '1 Year' },
];

function KPI({ label, value, sub, trend, icon: Icon, color = '#561D1A' }) {
  const isPositive = trend > 0;
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between mb-2">
        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">{label}</span>
        {Icon && <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}><Icon size={14} style={{ color }} /></div>}
      </div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      <div className="flex items-center gap-2 mt-1">
        {sub && <span className="text-[11px] text-gray-400">{sub}</span>}
        {trend !== undefined && trend !== null && (
          <span className={`text-[11px] font-medium flex items-center gap-0.5 ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
            {isPositive ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
    </div>
  );
}

function SectionCard({ title, children, className = '' }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-100 p-5 ${className}`}>
      <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">{title}</h3>
      {children}
    </div>
  );
}

function fmt(n) { return typeof n === 'number' ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : n; }

// ════════════════════════════════════════════════════════════════════
// ACCOUNTS DASHBOARD
// ════════════════════════════════════════════════════════════════════

function AccountsDashboard({ data }) {
  const { currency: activeCurrency } = useCurrency();
  const fmtC = (n) => formatAmount(n, activeCurrency);
  if (!data) return null;
  const s = data.summary;
  return (
    <div className="space-y-5" data-testid="accounts-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <KPI label="Vouchers Issued" value={fmt(s.total_issued_count)} sub={fmtC(s.total_issued_value)} icon={Ticket} color="#2563EB" />
        <KPI label="Redeemed" value={fmt(s.total_redeemed_count)} sub={fmtC(s.total_redeemed_value)} icon={DollarSign} color="#059669" />
        <KPI label="Outstanding Liability" value={fmtC(s.outstanding_liability)} sub={`${s.active_voucher_count} active`} icon={AlertTriangle} color="#DC2626" />
        <KPI label="Expired" value={fmt(s.expired_count)} sub={fmtC(s.expired_value)} icon={Clock} color="#F59E0B" />
        <KPI label="Redemption Rate" value={`${s.redemption_rate}%`} sub={`${s.blocked_count} blocked (${fmtC(s.blocked_value)})`} icon={TrendingUp} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SectionCard title="Daily Reconciliation">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.daily_reconciliation}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => fmtC(v)} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="sales_value" name="Sales" fill="#561D1A" fillOpacity={0.1} stroke="#561D1A" strokeWidth={2} />
              <Area type="monotone" dataKey="redeemed_value" name="Redeemed" fill="#D4AF37" fillOpacity={0.1} stroke="#D4AF37" strokeWidth={2} />
              <Area type="monotone" dataKey="issued_value" name="Issued" fill="#2563EB" fillOpacity={0.05} stroke="#2563EB" strokeWidth={1} />
            </AreaChart>
          </ResponsiveContainer>
        </SectionCard>

        <SectionCard title="Store-wise Redemption">
          <div className="space-y-2 max-h-[260px] overflow-y-auto">
            {(data.store_breakdown || []).map((st, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                <div className="w-1 h-8 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{st.store_name}</p>
                  <p className="text-[10px] text-gray-400">{st.category} &middot; {st.transaction_count} transactions</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-gray-800">{fmtC(st.total_redeemed)}</p>
                  <p className="text-[10px] text-gray-400">Avg: {fmtC(st.avg_transaction)}</p>
                </div>
              </div>
            ))}
            {(data.store_breakdown || []).length === 0 && <p className="text-gray-400 text-xs text-center py-4">No redemptions in this period</p>}
          </div>
        </SectionCard>
      </div>

      {(data.fraud_alerts || []).length > 0 && (
        <SectionCard title="Fraud / Anomaly Alerts">
          <div className="space-y-2">
            {(data.fraud_alerts || []).map((a, i) => (
              <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${a.severity === 'high' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                <ShieldAlert size={16} className={a.severity === 'high' ? 'text-red-500' : 'text-amber-500'} />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-800">{a.merchant_name} &mdash; {a.redemption_count} redemptions on {a.date}</p>
                  <p className="text-[10px] text-gray-500">Total: {fmtC(a.total_value)}</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${a.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{a.severity}</span>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// FINANCE DASHBOARD
// ════════════════════════════════════════════════════════════════════

function FinanceDashboard({ data }) {
  const { currency: activeCurrency } = useCurrency();
  const fmtC = (n) => formatAmount(n, activeCurrency);
  if (!data) return null;
  const s = data.summary;
  const pieData = [
    { name: 'Redeemed', value: s.redeemed_value },
    { name: 'Expired (Breakage)', value: s.expired_value },
    { name: 'Deferred (Active)', value: s.deferred_revenue },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-5" data-testid="finance-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <KPI label="Recognized Revenue" value={fmtC(s.recognized_revenue)} sub="Redeemed + Expired" icon={DollarSign} color="#059669" />
        <KPI label="Deferred Revenue" value={fmtC(s.deferred_revenue)} sub={`${s.deferred_count} active vouchers`} icon={Clock} color="#DC2626" />
        <KPI label="Total Sales" value={fmtC(s.total_sales)} sub={`${s.total_sales_count} orders`} icon={TrendingUp} color="#2563EB" />
        <KPI label="Breakage Rate" value={`${s.breakage_rate}%`} sub={`${s.expired_count} expired`} icon={AlertTriangle} color="#F59E0B" />
        <KPI label="Expiring Soon" value={fmt(s.expiring_soon_count)} sub={`${fmtC(s.expiring_soon_value)} in 30d`} icon={Clock} color="#7C3AED" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gradient-to-br from-[#561D1A]/5 to-transparent rounded-xl p-4 border border-gray-100">
          <p className="text-[10px] text-gray-400 uppercase">VAT on Sales (16%)</p>
          <p className="text-lg font-bold text-[#561D1A] mt-1">{fmtC(s.vat_on_sales)}</p>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-transparent rounded-xl p-4 border border-gray-100">
          <p className="text-[10px] text-gray-400 uppercase">Net Sales (ex-VAT)</p>
          <p className="text-lg font-bold text-emerald-700 mt-1">{fmtC(s.net_sales_ex_vat)}</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-transparent rounded-xl p-4 border border-gray-100">
          <p className="text-[10px] text-gray-400 uppercase">Settled</p>
          <p className="text-lg font-bold text-blue-700 mt-1">{fmtC(s.total_settled)}</p>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-transparent rounded-xl p-4 border border-gray-100">
          <p className="text-[10px] text-gray-400 uppercase">Pending Settlement</p>
          <p className="text-lg font-bold text-amber-700 mt-1">{fmtC(s.total_pending_settlement)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <SectionCard title="Revenue Trend" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.revenue_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => fmtC(v)} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="sales" name="Sales" fill="#059669" fillOpacity={0.1} stroke="#059669" strokeWidth={2} />
              <Area type="monotone" dataKey="redeemed" name="Redeemed" fill="#D4AF37" fillOpacity={0.1} stroke="#D4AF37" strokeWidth={2} />
              <Line type="monotone" dataKey="net_revenue" name="Net Revenue" stroke="#561D1A" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </SectionCard>

        <SectionCard title="Revenue Composition">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={3} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                {pieData.map((_, i) => <Cell key={i} fill={['#059669', '#DC2626', '#F59E0B'][i]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtC(v)} />
            </PieChart>
          </ResponsiveContainer>
        </SectionCard>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// MARKETING DASHBOARD
// ════════════════════════════════════════════════════════════════════

function MarketingDashboard({ data }) {
  const { currency: activeCurrency } = useCurrency();
  const fmtC = (n) => formatAmount(n, activeCurrency);
  if (!data) return null;
  const s = data.summary;
  return (
    <div className="space-y-5" data-testid="marketing-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <KPI label="Unique Shoppers" value={fmt(s.unique_shoppers)} sub={`of ${s.total_shoppers} total`} icon={Users} color="#2563EB" />
        <KPI label="New Shoppers" value={fmt(s.new_shoppers)} icon={Users} color="#059669" />
        <KPI label="Repeat Buyers" value={fmt(s.repeat_buyers)} sub={`${s.retention_rate}% retention`} icon={TrendingUp} color="#7C3AED" />
        <KPI label="Conversion Rate" value={`${s.overall_conversion_rate}%`} sub="Issued to Redeemed" icon={Megaphone} color="#D4AF37" />
        <KPI label="Total Redeemed" value={fmt(s.total_vouchers_redeemed)} sub={`of ${s.total_vouchers_issued} issued`} icon={Ticket} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SectionCard title="Campaign / Category Performance">
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {(data.category_performance || []).map((cp, i) => {
              const allVals = (data.category_performance || []).map(c => c.issued_value);
              const maxVal = allVals.length > 0 ? Math.max(...allVals, 1) : 1;
              return (
                <div key={i} className="py-2 border-b border-gray-50 last:border-0">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{cp.name}</p>
                      <p className="text-[10px] text-gray-400">{cp.category || 'gift'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-bold text-[#561D1A]">{cp.conversion_rate}% conv.</p>
                      <p className="text-[10px] text-gray-400">{cp.redeemed_count}/{cp.issued_count}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 h-2">
                    <div className="rounded-l bg-[#561D1A]" style={{ width: `${(cp.redeemed_value / maxVal) * 100}%` }} title={`Redeemed: ${fmtC(cp.redeemed_value)}`} />
                    <div className="rounded-r bg-gray-200" style={{ width: `${((cp.issued_value - cp.redeemed_value) / maxVal) * 100}%` }} title={`Unredeemed: ${fmtC(cp.issued_value - cp.redeemed_value)}`} />
                  </div>
                </div>
              );
            })}
            {(data.category_performance || []).length === 0 && <p className="text-gray-400 text-xs text-center py-4">No data</p>}
          </div>
        </SectionCard>

        <SectionCard title="Coupon Usage Trend">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.usage_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="issued" name="Issued" fill="#2563EB" radius={[3, 3, 0, 0]} />
              <Bar dataKey="redeemed" name="Redeemed" fill="#059669" radius={[3, 3, 0, 0]} />
              <Bar dataKey="purchases" name="Purchases" fill="#D4AF37" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>
      </div>

      {(data.customer_segments || []).length > 0 && (
        <SectionCard title="Customer Segmentation by Spend">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.customer_segments} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis dataKey="tier" type="category" tick={{ fontSize: 10 }} width={70} />
              <Tooltip formatter={(v, name) => name === 'total_spent' ? fmtC(v) : v} />
              <Bar dataKey="customer_count" name="Customers" fill="#561D1A" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// MALL MANAGEMENT DASHBOARD
// ════════════════════════════════════════════════════════════════════

function MallDashboard({ data }) {
  const { currency: activeCurrency } = useCurrency();
  const fmtC = (n) => formatAmount(n, activeCurrency);
  if (!data) return null;
  const s = data.summary;
  return (
    <div className="space-y-5" data-testid="mall-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <KPI label="Vouchers in Circulation" value={fmt(s.total_vouchers_in_circulation)} sub={`${s.active_vouchers} active`} icon={Ticket} color="#2563EB" />
        <KPI label="Active Value" value={fmtC(s.active_value)} icon={DollarSign} color="#059669" />
        <KPI label="Period Sales" value={fmtC(s.period_sales)} sub={`${s.period_sales_count} orders`} trend={s.sales_growth} icon={TrendingUp} color="#561D1A" />
        <KPI label="Period Redeemed" value={fmtC(s.period_redeemed)} sub={`${s.period_redeemed_count} tx`} trend={s.redemption_growth} icon={DollarSign} color="#D4AF37" />
        <KPI label="Platinum Programme" value={`${s.platinum_members} members`} sub={`${fmtC(s.platinum_discounts_given)} discounts`} icon={CreditCard} color="#7C3AED" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <SectionCard title="Overall Trend" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => fmtC(v)} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="sales" name="Sales" fill="#561D1A" fillOpacity={0.1} stroke="#561D1A" strokeWidth={2} />
              <Area type="monotone" dataKey="redeemed" name="Redeemed" fill="#D4AF37" fillOpacity={0.1} stroke="#D4AF37" strokeWidth={2} />
              <Line type="monotone" dataKey="vouchers_issued" name="Vouchers Issued" stroke="#2563EB" strokeWidth={1.5} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </SectionCard>

        <SectionCard title="Revenue by Category">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.revenue_by_category || []} cx="50%" cy="50%" innerRadius={45} outerRadius={85} dataKey="total" nameKey="category" paddingAngle={2}
                label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                {(data.revenue_by_category || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtC(v)} />
            </PieChart>
          </ResponsiveContainer>
        </SectionCard>
      </div>

      <SectionCard title="Top Performing Stores">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 text-gray-400 uppercase tracking-wider font-medium">#</th>
                <th className="text-left py-2 text-gray-400 uppercase tracking-wider font-medium">Store</th>
                <th className="text-left py-2 text-gray-400 uppercase tracking-wider font-medium">Category</th>
                <th className="text-right py-2 text-gray-400 uppercase tracking-wider font-medium">Redeemed</th>
                <th className="text-right py-2 text-gray-400 uppercase tracking-wider font-medium">Transactions</th>
                <th className="text-right py-2 text-gray-400 uppercase tracking-wider font-medium">Avg Ticket</th>
              </tr>
            </thead>
            <tbody>
              {(data.top_stores || []).map((st, i) => (
                <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-2.5 text-gray-400 font-mono">{i + 1}</td>
                  <td className="py-2.5 font-medium text-gray-800">{st.store_name}</td>
                  <td className="py-2.5 text-gray-500">{st.category}</td>
                  <td className="py-2.5 text-right font-bold text-[#561D1A]">{fmtC(st.total_redeemed)}</td>
                  <td className="py-2.5 text-right text-gray-600">{st.transaction_count}</td>
                  <td className="py-2.5 text-right text-gray-600">{fmtC(st.avg_ticket_size)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// MAIN REPORTS TAB
// ════════════════════════════════════════════════════════════════════

const DASHBOARDS = [
  { id: 'accounts', label: 'Accounts', icon: Calculator, color: '#2563EB', endpoint: '/api/store/analytics/accounts' },
  { id: 'finance', label: 'Finance', icon: DollarSign, color: '#059669', endpoint: '/api/store/analytics/finance' },
  { id: 'marketing', label: 'Marketing', icon: Megaphone, color: '#7C3AED', endpoint: '/api/store/analytics/marketing' },
  { id: 'mall', label: 'Mall Mgmt', icon: Building2, color: '#561D1A', endpoint: '/api/store/analytics/mall' },
];

export default function ReportsTab() {
  const [activeDash, setActiveDash] = useState('accounts');
  const [period, setPeriod] = useState('365d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { currency: activeCurrency } = useCurrency();

  const loadData = useCallback(async () => {
    const dash = DASHBOARDS.find(d => d.id === activeDash);
    if (!dash) return;
    setLoading(true);
    try {
      const res = await api.get(dash.endpoint, { params: { period } });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load report data');
    }
    setLoading(false);
  }, [activeDash, period]);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <div className="space-y-4" data-testid="reports-tab">
      {/* Dashboard Selector + Period */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1.5 bg-gray-100 p-1 rounded-xl">
          {DASHBOARDS.map(d => {
            const Icon = d.icon;
            const active = activeDash === d.id;
            return (
              <button key={d.id} onClick={() => setActiveDash(d.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${active ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid={`dash-tab-${d.id}`}>
                <Icon size={13} style={active ? { color: d.color } : {}} />
                {d.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select value={period} onChange={e => setPeriod(e.target.value)}
              className="appearance-none bg-white border border-gray-200 rounded-lg pl-3 pr-8 py-2 text-xs outline-none focus:border-[#561D1A] cursor-pointer"
              data-testid="period-selector">
              {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
          <button onClick={loadData} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50" data-testid="refresh-btn">
            <RefreshCw size={13} className={`text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={async () => {
              const dash = DASHBOARDS.find(d => d.id === activeDash);
              if (!dash) return;
              setDownloading(true);
              try {
                const token = localStorage.getItem('accessToken');
                const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
                const url = `${baseUrl}${dash.endpoint}/pdf?period=${period}`;
                const res = await fetch(url, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Currency': activeCurrency || 'KES',
                  },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = `LuxeMall_${dash.label.replace(/\s+/g, '_')}_Report.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(blobUrl);
                toast.success('PDF report downloaded');
              } catch (err) {
                console.error('PDF download error:', err);
                toast.error('Failed to generate PDF report');
              }
              setDownloading(false);
            }}
            disabled={downloading || loading || !data}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-[#561D1A] text-white hover:bg-[#6d2521] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="download-pdf-btn"
          >
            <Download size={13} className={downloading ? 'animate-pulse' : ''} />
            {downloading ? 'Generating...' : 'Download PDF'}
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <RefreshCw size={24} className="text-[#561D1A] animate-spin" />
        </div>
      )}

      {/* Dashboard Content */}
      {!loading && data && (
        <>
          {activeDash === 'accounts' && <AccountsDashboard data={data} />}
          {activeDash === 'finance' && <FinanceDashboard data={data} />}
          {activeDash === 'marketing' && <MarketingDashboard data={data} />}
          {activeDash === 'mall' && <MallDashboard data={data} />}
        </>
      )}
    </div>
  );
}
